import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import { computed, type Ref, unref, type MaybeRef } from "vue";

import type { components as OpenAPI } from "@/autogen/openapi";
import URLS from "@/autogen/urls";
import { sampleEmail } from "@/lib/editor/markdown/sample";
import type { PreviewContext } from "@/store/stores/ui-states";
import type { Newsletter } from "@/types/newsletter";

import api from "../../base";
import { useNewsletterId, useXsrfHeaders } from "../utils";
import { useLatestPublishedEmail } from "./list";
import { useEmailDetailQuery } from "./detail";

export type EmailTemplate = OpenAPI["schemas"]["NewsletterEmailTemplate"];

// Needs to map to the keys of `TRANSACTIONAL_EMAIL_KEY_TO_TRANSACTIONAL_EMAIL`.
export type TransactionalEmail =
  | "automation_notification"
  | "churn"
  | "confirm_subscription_reminder"
  | "confirm_subscription"
  | "gift_subscription"
  | "gift_unsubscription"
  | "premium_subscription_confirmed"
  | "subscription_confirmed";

type RenderOptions = {
  text?: string;
  subject?: string;
  id?: string;
  newsletter?: Newsletter;
  template?: EmailTemplate;
  transactional_email?: TransactionalEmail;
  external_feed_id?: string;
  automation_id?: string;
  context?: Record<string, string | undefined>;
  subscriber_id?: string;
  previews: PreviewContext[];
};

type RawRenderResult = {
  result?: string;
  web?: string;
  portal?: string;
  subscribe?: string;
  archive?: string;
};

export type RenderResult = {
  email?: string;
  web?: string;
  portal?: string;
  subscribe?: string;
  archive?: string;
};

const useRenderMarkdown = () => {
  const headers = useXsrfHeaders();

  return async ({
    signal,
    options: data,
  }: {
    signal: AbortSignal;
    options: RenderOptions;
  }): Promise<RenderResult> => {
    const response = await fetch(URLS["render"](), {
      method: "post",
      signal: signal,
      headers: {
        ...headers.value,
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const json = (await response.json()) as RawRenderResult;

    return {
      email: json.result,
      web: json.web,
      portal: json.portal,
      subscribe: json.subscribe,
      archive: json.archive,
    };
  };
};

export type DraftPreviewOptions =
  | {
      type: "newsletter_draft";
      newsletter: Newsletter;
      previews: PreviewContext[];
      selectedEmailId?: string;
      subscriberId?: string;
    }
  | {
      type: "email_draft";
      email: {
        id?: string;
        subject: string;
        body: string;
        template?: EmailTemplate;
        description?: string;
      };
      previews: PreviewContext[];
      newsletter?: Newsletter;
      subscriberId?: string;
    }
  | {
      type: "transactional_draft";
      email: {
        type: TransactionalEmail;
        subject: string;
        body: string;
        template?: EmailTemplate;
      };
      previews: PreviewContext[];
      subscriberId?: string;
    }
  | {
      type: "feed_draft";
      id: string;
      email: {
        subject: string;
        body: string;
      };
      previews: PreviewContext[];
      subscriberId?: string;
    };

export const useDraftPreviewQuery = ({
  options,
  enabled,
}: {
  options: MaybeRef<DraftPreviewOptions>;
  enabled?: Ref<boolean>;
}) => {
  const newsletterId = useNewsletterId();
  const render = useRenderMarkdown();

  const isEnabled = computed(() => {
    return unref(enabled) ?? true;
  });

  const latestEmail = useLatestPublishedEmail({
    enabled: computed((): boolean => {
      if (!isEnabled.value) {
        return false;
      }

      const opts = unref(options);
      return opts.type === "newsletter_draft" && !opts.selectedEmailId;
    }),
  });

  const selectedEmail = useEmailDetailQuery({
    id: computed(() => {
      const opts = unref(options);
      return opts.type === "newsletter_draft" && opts.selectedEmailId
        ? opts.selectedEmailId
        : undefined;
    }),
    enabled: computed(() => {
      if (!isEnabled.value) {
        return false;
      }

      const opts = unref(options);
      return opts.type === "newsletter_draft" && !!opts.selectedEmailId;
    }),
  });

  const queryParams = computed(() => {
    const $options = unref(options);

    return {
      ...$options,
      latestEmail:
        $options.type === "newsletter_draft" && !$options.selectedEmailId
          ? latestEmail.value?.id
          : undefined,
      selectedEmailId:
        $options.type === "newsletter_draft" && $options.selectedEmailId
          ? selectedEmail.data.value?.id
          : undefined,
    };
  });

  const query = useQuery({
    queryKey: [newsletterId, "emails", "preview", queryParams],
    enabled: computed(() => {
      if (!isEnabled.value) {
        return false;
      }

      const opts = unref(options);

      // Wait for selected email to load if one is selected
      if (opts.type === "newsletter_draft" && opts.selectedEmailId) {
        return selectedEmail.isSuccess.value || selectedEmail.isError.value;
      }

      return true;
    }),
    gcTime: 10_000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    async queryFn({ signal }) {
      const $options = unref(options);
      const $latestEmail = unref(latestEmail);
      const $selectedEmail = selectedEmail.data.value;

      let config: RenderOptions;
      switch ($options.type) {
        case "email_draft": {
          const email = $options.email;

          config = {
            id: email.id,
            subject: email.subject,
            text: email.body,
            template: email.template,
            context: {
              description: email.description,
            },
            newsletter: $options.newsletter,
            subscriber_id: $options.subscriberId,
            previews: $options.previews,
          };
          break;
        }
        case "feed_draft": {
          const email = $options.email;

          config = {
            external_feed_id: $options.id,
            subject: email.subject,
            text: email.body,
            subscriber_id: $options.subscriberId,
            previews: $options.previews,
          };
          break;
        }
        case "newsletter_draft": {
          // Use selected email if provided, otherwise fall back to latest email
          const emailToUse = $selectedEmail || $latestEmail;

          config = {
            newsletter: $options.newsletter,
            subscriber_id: $options.subscriberId,
            previews: $options.previews,
            ...(emailToUse
              ? {
                  subject: emailToUse.subject,
                  text: emailToUse.id,
                  id: emailToUse.id,
                }
              : {
                  subject: sampleEmail.subject,
                  text: sampleEmail.body,
                }),
          };
          break;
        }
        case "transactional_draft": {
          const email = $options.email;

          config = {
            transactional_email: email.type,
            subject: email.subject,
            text: email.body,
            template: email.template,
            subscriber_id: $options.subscriberId,
            previews: $options.previews,
          };
          break;
        }
      }

      const rendered = await render({ signal, options: config });

      return rendered;
    },
    placeholderData: keepPreviousData,
  });

  return query;
};

export const useBasicEmailPreviewQuery = ({ id }: { id: MaybeRef<string> }) => {
  const newsletterId = useNewsletterId();

  const query = useQuery({
    queryKey: [newsletterId, "emails", "basic-preview", id],
    staleTime: 60_000,
    async queryFn({ signal }) {
      const emailId = unref(id);
      const response = await api.get("/emails/{id}/renders", {
        signal,
        params: {
          path: {
            id: emailId,
          },
          query: {
            target: "html",
          },
        },
      });

      return response.data?.content ?? "";
    },
  });

  return query;
};
